/**
 * Chat xabarlaridan keladigan push-turidagi bildirishnomalar (localStorage).
 * Backend ulanganda shu yozuvlar server bilan almashtiriladi.
 */

import type { User } from '../auth/authContext';
import { supportChatTenantId } from './supportChat';
import type { SupportAuthorRole, SupportMessage } from './supportChat';

const STORAGE_KEY = 'neduz_chat_push_inbox';
const MAX_ITEMS = 200;
const CHANGED_EVENT = 'chat-push-inbox-changed';

export interface ChatPushRecord {
  id: string;
  messageId: string;
  tenantId: string;
  tenantRegionId?: string;
  companyName?: string;
  fromMijoz: boolean;
  authorRole: SupportAuthorRole;
  authorName: string;
  preview: string;
  createdAt: string;
  read: boolean;
}

function loadAll(): ChatPushRecord[] {
  try {
    const raw = window.localStorage?.getItem?.(STORAGE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return [];
    return v.filter(
      (x): x is ChatPushRecord =>
        x &&
        typeof x === 'object' &&
        typeof x.id === 'string' &&
        typeof x.messageId === 'string' &&
        typeof x.tenantId === 'string' &&
        typeof x.fromMijoz === 'boolean' &&
        typeof x.preview === 'string' &&
        typeof x.createdAt === 'string' &&
        typeof x.read === 'boolean',
    );
  } catch {
    return [];
  }
}

function saveAll(list: ChatPushRecord[]) {
  try {
    const trimmed = list.slice(-MAX_ITEMS);
    window.localStorage?.setItem?.(STORAGE_KEY, JSON.stringify(trimmed));
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT));
  } catch {}
}

export function subscribeChatPushInbox(cb: () => void): () => void {
  const onLocal = () => cb();
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener(CHANGED_EVENT, onLocal);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGED_EVENT, onLocal);
    window.removeEventListener('storage', onStorage);
  };
}

function previewFromMessage(m: SupportMessage): string {
  const t = m.text?.trim();
  if (t) return t.length > 160 ? `${t.slice(0, 157)}…` : t;
  if (m.attachmentType === 'image') return '📷';
  if (m.attachmentType === 'video') return '🎬';
  return '…';
}

/** Xabar saqlangandan keyin chaqiriladi */
export function recordChatPushFromMessage(
  m: SupportMessage,
  options?: { tenantRegionId?: string; companyName?: string },
): void {
  const fromMijoz = m.authorRole === 'tenant_owner';
  const row: ChatPushRecord = {
    id: `cp_${m.id}`,
    messageId: m.id,
    tenantId: m.tenantId,
    tenantRegionId: options?.tenantRegionId,
    companyName: options?.companyName,
    fromMijoz,
    authorRole: m.authorRole,
    authorName: m.authorName,
    preview: previewFromMessage(m),
    createdAt: m.createdAt,
    read: false,
  };
  const prev = loadAll();
  saveAll([...prev, row]);
}

export function markChatPushRead(id: string): void {
  const prev = loadAll();
  saveAll(prev.map((x) => (x.id === id ? { ...x, read: true } : x)));
}

export function markAllChatPushReadForUser(user: User | null): void {
  const visible = getChatPushNotificationsForUser(user).map((x) => x.id);
  if (!visible.length) return;
  const set = new Set(visible);
  const prev = loadAll();
  saveAll(prev.map((x) => (set.has(x.id) ? { ...x, read: true } : x)));
}

/** Joriy foydalanuvchi uchun ko‘rinadigan yozuvlar (yangisi birinchi) */
export function getChatPushNotificationsForUser(user: User | null): ChatPushRecord[] {
  if (!user?.role) return [];
  const all = loadAll();
  const role = user.role;
  const tenantKey = supportChatTenantId(user);

  const filtered = all.filter((n) => {
    if (role === 'tenant_owner') {
      return !n.fromMijoz && n.tenantId === tenantKey;
    }
    if (role === 'platform_owner' || role === 'general_manager') {
      return n.fromMijoz;
    }
    if (role === 'regional_manager') {
      if (!n.fromMijoz) return false;
      const rid = user.regionId?.trim();
      if (!n.tenantRegionId || !rid) return true;
      return String(n.tenantRegionId) === String(rid);
    }
    if (role === 'dispatcher') {
      return n.fromMijoz;
    }
    return false;
  });

  return filtered.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

export function getUnreadChatPushCount(user: User | null): number {
  return getChatPushNotificationsForUser(user).filter((n) => !n.read).length;
}

export function formatChatPushTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}
